import { z } from 'zod';
import { FailureDiagnosticEvidenceSchema } from './issue-evidence.js';
import { IssueSeveritySchema } from './issue-core.js';
import { DIAGNOSTIC_LIMIT, TITLE_LIMIT, URL_LIMIT } from './issues-schema-utils.js';

/**
 * Payload the failure-diagnostic agent submits per discrete issue via its
 * `file_issue` tool. Deliberately simpler than the legacy auto-file payload:
 * plain user language, a minimal repro path, and curated evidence — never the
 * full test's step dump.
 *
 * IMPORTANT: this schema is used as an AI tool input schema, so it must stay
 * representable in JSON Schema — no `.transform()` / `.pipe()` (see
 * `packages/ai/src/schema.ts`). Trimming/normalization happens in the filing
 * service, not here.
 */
export const FileIssuePayloadSchema = z.object({
  /** Plain user language naming the defect — never a "Flow X failed:" template. */
  title: z.string().min(1).max(TITLE_LIMIT),
  /** What actually happened, in the words a user of the app would use. */
  whatHappened: z.string().min(1).max(DIAGNOSTIC_LIMIT),
  /** What the test (and a user) expected to observe instead. */
  whatWasExpected: z.string().min(1).max(DIAGNOSTIC_LIMIT),
  /**
   * The QUICK path to reproduce — numbered markdown steps starting from the
   * closest reachable state, not a walkthrough of the whole flow.
   */
  reproSteps: z.string().min(1).max(DIAGNOSTIC_LIMIT),
  severity: IssueSeveritySchema,
  /** The URL where the defect is observable (http/https). */
  primaryUrl: z.string().max(URL_LIMIT).optional(),
  /** Curated evidence for this specific issue (frames, clips, logs, network). */
  evidence: z.array(FailureDiagnosticEvidenceSchema).max(20).optional(),
  /**
   * Attach this report as a new occurrence of an already-open issue on the same
   * flow instead of creating a duplicate.
   */
  existingIssueId: z.string().uuid().optional(),
});
export type FileIssuePayload = z.infer<typeof FileIssuePayloadSchema>;
